import { useEffect, useId, useState } from 'react';
import type { Limit } from '../engine/model/params';

interface Props {
  label: string;
  value: number;
  onChange: (v: number) => void;
  limit?: Pick<Limit, 'min' | 'max' | 'step'> & { unit?: string };
  unit?: string;
  decimals?: number;
  disabled?: boolean;
  title?: string;
}

/**
 * Precise numeric input: validates against limits while typing, applies valid values live,
 * shows the allowed range on invalid input, supports ↑/↓ (Shift = ×10) and resets on blur.
 */
export function NumberField({ label, value, onChange, limit, unit, decimals = 2, disabled, title }: Props) {
  const id = useId();
  const fmt = (v: number) => (Number.isFinite(v) ? String(Math.round(v * 10 ** decimals) / 10 ** decimals) : '');
  const [text, setText] = useState(fmt(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(fmt(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, focused]);

  const parse = (t: string): number => Number(t.replace(',', '.').trim());
  const valid = (n: number) => t(n);
  function t(n: number): boolean {
    if (!Number.isFinite(n)) return false;
    if (limit && (n < limit.min - 1e-9 || n > limit.max + 1e-9)) return false;
    return true;
  }
  const n = parse(text);
  const invalid = text.trim() !== '' && !valid(n);
  const u = unit ?? limit?.unit;

  const apply = (s: string) => {
    setText(s);
    const v = parse(s);
    if (s.trim() !== '' && valid(v) && v !== value) onChange(v);
  };

  return (
    <label className="field" htmlFor={id} title={title}>
      <span>{label}</span>
      <div className={'num' + (invalid ? ' invalid' : '')}>
        <input
          id={id}
          aria-label={label}
          inputMode="decimal"
          value={text}
          disabled={disabled}
          aria-invalid={invalid}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setText(fmt(value));
          }}
          onChange={(e) => apply(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault();
              const step = (limit?.step ?? 1) * (e.shiftKey ? 10 : 1);
              let v = (Number.isFinite(n) ? n : value) + (e.key === 'ArrowUp' ? step : -step);
              if (limit) v = Math.min(limit.max, Math.max(limit.min, v));
              apply(fmt(v));
            } else if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'Escape') {
              setText(fmt(value));
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
        {u && <span className="unit">{u}</span>}
      </div>
      {invalid && limit && (
        <span className="error-text" role="alert">
          {limit.min}–{limit.max}
          {u ? ' ' + u : ''}
        </span>
      )}
    </label>
  );
}
